import type { Identifier } from "ra-core";
import { useNotify, useTranslate } from "ra-core";
import { Copy } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useReferralCodes } from "./useReferralCodes";

export const CodeChip = ({ code }: { code: string }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      notify("tenax.agreements.codes.copied", {
        type: "info",
        messageArgs: { code },
      });
    } catch {
      notify("tenax.agreements.codes.copy_failed", { type: "error" });
    }
  };
  return (
    <span className="inline-flex items-center gap-0.5">
      <Badge variant="secondary" className="font-mono">
        {code}
      </Badge>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-7"
        aria-label={translate("tenax.agreements.codes.copy", { code })}
        onClick={(event) => {
          // inside a link (agreements list): copy, do not open
          event.preventDefault();
          event.stopPropagation();
          copy();
        }}
      >
        <Copy className="size-3.5" />
      </Button>
    </span>
  );
};

export const ReferralCodes = ({
  contactId,
  agreementId,
}: {
  contactId?: Identifier | null;
  agreementId?: Identifier | null;
}) => {
  const translate = useTranslate();
  const codes = useReferralCodes({ contactId, agreementId });
  if (!codes.length) return null;
  return (
    <div
      role="group"
      aria-label={translate("tenax.agreements.codes.title")}
      className="flex flex-wrap items-center gap-x-2 gap-y-1"
    >
      <span className="text-xs text-muted-foreground">
        {translate("tenax.agreements.codes.title")}
      </span>
      {codes.map((code) => (
        <CodeChip key={code.id} code={code.code} />
      ))}
    </div>
  );
};
